import { useEffect, useState } from 'react'
import { RangeBar } from './RangeBar'
import { SiteNav } from './SiteNav'
import { SiteFooter } from './SiteFooter'

const API_BASE = import.meta.env.DEV ? '' : import.meta.env.BASE_URL.replace(/\/$/, '')

interface ShareData {
  created_at: number
  tdee: { low: number; high: number; recommended: number; strategy: string }
  bmr: { kcal: number; method: string }
  components: {
    neat: { low: number; high: number }
    tef: { low: number; high: number }
    eat: { low: number; high: number }
  }
  traditional: { bucket: string; tdee_kcal: number }
  inputs_summary: string[]
  share_view_count: number
}

function extractToken(): string | null {
  // URL shape: /tdee/share/<token> or /share/<token>. Token is passed as-is
  // (no case normalization) — server lookups are case-insensitive and the
  // 2026-06 profile slugs (m-5ft10-176lb-6k-0604) are canonically lowercase.
  const m = window.location.pathname.match(/\/share\/([A-Za-z0-9-]{6,32})/)
  return m ? m[1] : null
}

function relativeDate(epochMs: number): string {
  const days = Math.floor((Date.now() - epochMs) / (1000 * 60 * 60 * 24))
  if (days <= 0) return 'today'
  if (days === 1) return 'yesterday'
  if (days < 7) return `${days} days ago`
  if (days < 30) return `${Math.floor(days / 7)} week${days < 14 ? '' : 's'} ago`
  return new Date(epochMs).toLocaleDateString()
}

const BUCKET_LABEL: Record<string, string> = {
  sedentary: 'Sedentary (×1.2)',
  light: 'Light activity (×1.375)',
  moderate: 'Moderate (×1.55)',
  active: 'Active (×1.725)',
  very_active: 'Very active (×1.9)',
}

export default function ShareView() {
  const [data, setData] = useState<ShareData | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const token = extractToken()

  useEffect(() => {
    document.title = 'The Most Accurate TDEE Calculator — Powered by AI'
  }, [])

  useEffect(() => {
    if (!token) {
      setError('That share link looks malformed.')
      setLoading(false)
      return
    }
    let cancelled = false
    void (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/share/${encodeURIComponent(token)}`)
        if (res.status === 404) throw new Error('Share link not found or expired.')
        if (!res.ok) throw new Error(`Server returned ${res.status}`)
        const json = (await res.json()) as ShareData
        if (!cancelled) setData(json)
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'failed to load')
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [token])

  return (
    <>
      <SiteNav />
      <div className="share-page">
        <header className="share-page-hero">
          <h1 className="hero-title">
            The <em>Most Accurate</em> TDEE Calculator — Powered by AI.
          </h1>
          <p className="share-page-sub">
            Total Daily Energy Expenditure — the calories someone&apos;s body actually burns
            in 24 hours, broken down into BMR + NEAT + TEF + EAT. They ran an AI interview
            of 5–7 questions instead of picking from a 5-bucket activity dropdown.
          </p>
        </header>

        {loading && <div className="share-loading">Loading result…</div>}

        {error && (
          <div className="share-error-state">
            <p>{error}</p>
            <a className="share-cta-primary" href={import.meta.env.BASE_URL}>
              Calculate yours →
            </a>
          </div>
        )}

        {data && <ShareBody data={data} />}
      </div>
      <SiteFooter />
    </>
  )
}

function ShareBody({ data }: { data: ShareData }) {
  const delta = data.tdee.recommended - data.traditional.tdee_kcal
  return (
    <>
      <div className="share-result-card">
        <header className="result-header">
          <span className="kicker">Their TDEE range</span>
          <span className="share-meta">calculated {relativeDate(data.created_at)}</span>
        </header>

        {data.inputs_summary && data.inputs_summary.length > 0 && (
          <ul className="share-inputs-summary">
            {data.inputs_summary.map((line, i) => (
              <li key={i}>{line}</li>
            ))}
          </ul>
        )}

        <div className="range-display">
          <div className="range-line">
            <span>{data.tdee.low}</span>
            <span className="range-dash">–</span>
            <span>{data.tdee.high}</span>
            <span className="range-unit">kcal/day</span>
          </div>
          <RangeBar low={data.tdee.low} high={data.tdee.high} marker={data.tdee.recommended} />
          <div className="recommended">
            <span className="label">Recommended</span>
            <span className="value">{data.tdee.recommended}</span>
            <span className="unit">kcal/day</span>
          </div>
        </div>

        <section className="work-section">
          <div className="work-section-header">
            <span className="kicker">How they got there</span>
          </div>
          <table className="breakdown">
            <tbody>
              <tr className="row-section">
                <th>
                  BMR
                  <span className="source-hint">
                    {data.bmr.method === 'mifflin' ? 'Mifflin-St Jeor (1990)' : 'Katch-McArdle'}
                  </span>
                </th>
                <td colSpan={2}>{data.bmr.kcal}</td>
              </tr>
              <tr className="row-section">
                <th>
                  NEAT
                  <span className="source-hint">Daily non-exercise activity</span>
                </th>
                <td>{data.components.neat.low}</td>
                <td>{data.components.neat.high}</td>
              </tr>
              <tr className="row-section">
                <th>
                  TEF
                  <span className="source-hint">Thermic effect of food</span>
                </th>
                <td>{data.components.tef.low}</td>
                <td>{data.components.tef.high}</td>
              </tr>
              <tr className="row-section">
                <th>
                  EAT
                  <span className="source-hint">Deliberate exercise (Ainsworth 2011 MET)</span>
                </th>
                <td>{data.components.eat.low}</td>
                <td>{data.components.eat.high}</td>
              </tr>
            </tbody>
          </table>
        </section>

        <p className="share-vs-traditional">
          A traditional <strong>{BUCKET_LABEL[data.traditional.bucket] ?? data.traditional.bucket}</strong>
          {' '}calculator would have said <strong>{data.traditional.tdee_kcal}</strong> kcal/day —{' '}
          <strong className={delta >= 0 ? 'share-delta-up' : 'share-delta-down'}>
            {delta >= 0 ? '+' : ''}
            {delta} kcal off
          </strong>
          {' '}from this calibrated number.
        </p>
      </div>

      <div className="share-cta-card">
        <h2 className="share-cta-headline">Get your own AI-calibrated TDEE.</h2>
        <p className="share-cta-sub">
          Free, no account, ~5 minutes. The AI interviews your day, then shows BMR + NEAT +
          TEF + EAT broken out — every number cited.
        </p>
        <a className="share-cta-primary" href={import.meta.env.BASE_URL}>
          Calculate yours →
        </a>
      </div>
    </>
  )
}
